// src/prototype/pages/EAWeightsPage.tsx
// ============================================================
// EA WEIGHT SETTINGS PAGE
//
// Visual chrome matches the Decision Brief / ROI Model (journey bar,
// sticky back bar, identity header). Category and measure sliders stay
// API-backed (GET/PUT /api/v2/weights).
//
// Route: /ea/weights
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Scale,
  Layers,
  AlertTriangle,
  Check,
  Loader2,
} from 'lucide-react';
import { useEA } from '../contexts/EAContext';
import { useAuth } from '@/contexts/AuthContext';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { EA_CLIENT_ID } from '../constants/eaClient';
import {
  CATEGORY_DEFINITIONS,
  MEASURE_DEFINITIONS,
  getDefaultCategoryWeightsMap,
  getDefaultMeasureWeightsMap,
  normalizeToTotal,
} from '../utils/weighting';
import { HelpPopover } from '../components/help/HelpPopover';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { CategoryDefinition, MeasureDefinition } from '../utils/weighting';

// ============================================================
// TYPES
// ============================================================

type TabType = 'categories' | 'measures';

interface LocationState {
  returnTo?: string;
  restoreScrollY?: number;
  returnToTab?: string;
}

type PendingWeightChange =
  | { kind: 'category'; key: string; value: number }
  | { kind: 'measure'; categoryKey: string; measureKey: string; value: number };

const API_CATEGORY_TO_UI_KEY: Record<string, string> = {
  revenue_and_cost: 'costSavings',
  strategic_alignment: 'strategicAlignment',
  time_to_value: 'timeToValue',
  data_readiness: 'dataReadiness',
  regulatory_compliance: 'compliance',
  org_readiness: 'orgReadiness',
};

const API_MEASURE_TO_UI_KEY: Record<string, string> = {
  revenue_generation: 'revenueGeneration',
  cost_savings: 'costSavings',
  long_term_value_creation: 'longTermValue',
  financial_risk_factors: 'financialRisk',
  core_mission_support: 'coreMission',
  competitive_differentiation: 'competitive',
  value_based_care_integration: 'valueBased',
  strategic_growth: 'strategicGrowth',
  operational_efficiency: 'operationalEfficiency',
  technology_alignment: 'techAlignment',
  revenue_diversification: 'revenueDiversification',
  implementation_complexity: 'implementationComplexity',
  market_readiness: 'marketReadiness',
  speed_to_generate_returns: 'speedToReturns',
  stakeholder_engagement: 'stakeholderEngagement',
  pilot_feasibility: 'pilotFeasibility',
  regulatory_approval_speed: 'regulatorySpeed',
  data_quality: 'dataQuality',
  data_standardization: 'dataStandardization',
  data_accessibility: 'dataAccessibility',
  data_transformation: 'dataTransformation',
  interoperability: 'interoperability',
  historical_data: 'historicalData',
  real_time_capabilities: 'realTime',
  security_and_privacy: 'securityPrivacy',
  de_identification: 'deIdentification',
  regulatory_frameworks: 'regulatoryFramework',
  legal_clarity: 'legalClarity',
  auditability: 'auditability',
  vendor_compliance: 'vendorCompliance',
  jurisdictional_complexity: 'jurisdictional',
  patient_consent: 'patientConsent',
  technical_infrastructure: 'techInfra',
  staff_expertise: 'staffExpertise',
  organizational_processes: 'orgProcesses',
  change_management: 'changeManagement',
  resource_allocation: 'resourceAllocation',
  partnerships: 'partnerships',
};

const UI_MEASURE_CATEGORY_BY_KEY: Record<string, string> = {
  revenueGeneration: 'costSavings',
  costSavings: 'costSavings',
  longTermValue: 'costSavings',
  financialRisk: 'costSavings',
  coreMission: 'strategicAlignment',
  competitive: 'strategicAlignment',
  valueBased: 'strategicAlignment',
  strategicGrowth: 'strategicAlignment',
  operationalEfficiency: 'strategicAlignment',
  techAlignment: 'strategicAlignment',
  revenueDiversification: 'strategicAlignment',
  implementationComplexity: 'timeToValue',
  marketReadiness: 'timeToValue',
  speedToReturns: 'timeToValue',
  stakeholderEngagement: 'timeToValue',
  pilotFeasibility: 'timeToValue',
  regulatorySpeed: 'timeToValue',
  dataQuality: 'dataReadiness',
  dataStandardization: 'dataReadiness',
  dataAccessibility: 'dataReadiness',
  dataTransformation: 'dataReadiness',
  interoperability: 'dataReadiness',
  historicalData: 'dataReadiness',
  realTime: 'dataReadiness',
  securityPrivacy: 'compliance',
  deIdentification: 'compliance',
  regulatoryFramework: 'compliance',
  legalClarity: 'compliance',
  auditability: 'compliance',
  vendorCompliance: 'compliance',
  jurisdictional: 'compliance',
  patientConsent: 'compliance',
  techInfra: 'orgReadiness',
  staffExpertise: 'orgReadiness',
  orgProcesses: 'orgReadiness',
  changeManagement: 'orgReadiness',
  resourceAllocation: 'orgReadiness',
  partnerships: 'orgReadiness',
};

// Reverse maps: UI camelCase key → API snake_case key
const UI_CATEGORY_TO_API_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(API_CATEGORY_TO_UI_KEY).map(([api, ui]) => [ui, api])
);

const UI_MEASURE_TO_API_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(API_MEASURE_TO_UI_KEY).map(([api, ui]) => [ui, api])
);

function buildApiPayload(
  categoryWeights: Record<string, number>,
  measureWeights: Record<string, Record<string, number>>
): Record<string, number> {
  const payload: Record<string, number> = {};

  Object.entries(categoryWeights).forEach(([uiKey, value]) => {
    const apiKey = UI_CATEGORY_TO_API_KEY[uiKey];
    if (apiKey) payload[apiKey] = value;
  });

  Object.values(measureWeights).forEach(categoryMeasures => {
    Object.entries(categoryMeasures).forEach(([uiKey, value]) => {
      const apiKey = UI_MEASURE_TO_API_KEY[uiKey];
      if (apiKey) payload[apiKey] = value;
    });
  });

  return payload;
}

// ============================================================
// NORMALIZE UTILITY
// ============================================================

function normalizeWeights(weights: Record<string, number>): Record<string, number> {
  return normalizeToTotal(weights, 100);
}

// Auto-redistribute weights: when one weight is set, distribute remaining 100% among other sliders
// Uses "largest remainder method" to guarantee sum = exactly 100%
function autoDistributeWeights(
  weights: Record<string, number>,
  changedKey: string,
  changedValue: number
): Record<string, number> {
  // Clamp to 0-100 range
  const clampedValue = Math.max(0, Math.min(100, Math.round(changedValue)));
  const otherKeys = Object.keys(weights).filter((k) => k !== changedKey);

  if (otherKeys.length === 0) {
    return { [changedKey]: 100 };
  }

  // Calculate base value for other sliders using largest remainder method
  const remaining = 100 - clampedValue;
  const basePerSlider = Math.floor(remaining / otherKeys.length);
  const carryover = remaining - (basePerSlider * otherKeys.length);

  // Build result: distribute carryover to first few sliders
  const result: Record<string, number> = { [changedKey]: clampedValue };

  otherKeys.forEach((key, index) => {
    // First 'carryover' sliders get +1 to distribute remainder
    // All values are integers
    result[key] = Math.round(basePerSlider + (index < carryover ? 1 : 0));
  });

  // Verify sum is exactly 100
  const sum = Object.values(result).reduce((a, b) => a + b, 0);
  if (sum !== 100) {
    console.error('Weight distribution error: sum is', sum, 'expected 100. Result:', result);
  }

  return result;
}

// ============================================================
// Shared weight % input — digits only, 0–100, supports typing e.g. 11 → 22
// ============================================================

function parseWeightInput(raw: string): number | null {
  const cleaned = String(raw).replace(/[^\d]/g, '');
  if (cleaned === '') return null;
  const n = Number.parseInt(cleaned, 10);
  if (!Number.isFinite(n)) return null;
  return Math.min(100, Math.max(0, n));
}

interface WeightPercentInputProps {
  value: number;
  onCommit: (value: number) => void;
  className?: string;
  widthClass?: string;
}

const WeightPercentInput: React.FC<WeightPercentInputProps> = ({
  value,
  onCommit,
  className = '',
  widthClass = 'w-14',
}) => {
  const rounded = Math.round(value);
  const [draft, setDraft] = useState(String(rounded));
  const [focused, setFocused] = useState(false);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Keep draft in sync with parent whenever we're not actively typing
  useEffect(() => {
    if (!focused) {
      setDraft(String(rounded));
    }
  }, [rounded, focused]);

  const commit = (raw: string) => {
    const parsed = parseWeightInput(raw);
    const next = parsed == null ? rounded : parsed;
    setDraft(String(next));
    if (next !== rounded) {
      onCommit(next);
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={3}
      aria-label="Weight percent"
      value={draft}
      onFocus={(e) => {
        setFocused(true);
        setDraft(String(rounded));
        e.target.select();
      }}
      onBlur={(e) => {
        // Prefer DOM value — avoids stale React state if blur races with the last keystroke
        const raw = e.target.value || draftRef.current;
        setFocused(false);
        commit(raw);
      }}
      onChange={(e) => {
        // Digits only, max 3 chars — commit on blur/Enter so typing 11→22 works cleanly
        const raw = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
        draftRef.current = raw;
        setDraft(raw);
      }}
      onKeyDown={(e) => {
        // Block scientific/sign/decimal keys
        if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
          e.preventDefault();
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      className={`${widthClass} px-2 py-1.5 text-lg font-bold text-[#18365E] text-center bg-white
                 focus:outline-none focus:ring-2 focus:ring-[#18365E]/20 ${className}`}
    />
  );
};

// ============================================================
// CATEGORY WEIGHT SLIDER COMPONENT (manual % + slider)
// ============================================================

interface CategorySliderProps {
  categoryKey: string;
  definition: CategoryDefinition;
  value: number;
  onChange: (key: string, value: number) => void;
  onReset: () => void;
  isDefault: boolean;
}

const CategorySlider: React.FC<CategorySliderProps> = ({
  categoryKey,
  definition,
  value,
  onChange,
  onReset,
  isDefault,
}) => {
  const Icon = definition.icon;
  const dimensionColor = definition.dimension === 'Business Value'
    ? 'bg-[#18365E]/10 text-[#18365E]'
    : 'bg-[#FF6E4C]/10 text-[#FF6E4C]';
  const dimensionBadgeColor = definition.dimension === 'Business Value'
    ? 'bg-[#18365E]/10 text-[#18365E] border-[#18365E]/20'
    : 'bg-[#FF6E4C]/10 text-[#FF6E4C] border-[#FF6E4C]/20';

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(categoryKey, Number(e.target.value));
  };

  return (
    <div className="p-4 bg-white rounded-lg border border-[#e5e7eb] hover:border-[#18365E]/20 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${dimensionColor}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-[#18365E]">{definition.shortLabel}</h3>
              {!isDefault && (
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                  Modified
                </span>
              )}
            </div>
            <p className="text-[10px] text-[#717182] mt-0.5 line-clamp-1">{definition.description}</p>
            <span className={`inline-block mt-1 text-[9px] font-medium px-2 py-0.5 rounded border ${dimensionBadgeColor}`}>
              {definition.dimension}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden bg-white">
            <WeightPercentInput
              value={value}
              onCommit={(next) => onChange(categoryKey, next)}
              widthClass="w-14"
              className="text-lg py-1.5"
            />
            <span className="px-2 py-1.5 bg-[#F5F7FA] text-sm text-[#717182] font-medium">%</span>
          </div>
          {!isDefault && (
            <button
              onClick={onReset}
              className="p-1.5 text-[#717182] hover:text-[#18365E] hover:bg-[#F5F7FA] rounded transition-colors"
              title="Reset to default"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[10px] text-[#717182] w-5">0</span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={handleSliderChange}
        style={{ background: `linear-gradient(to right, #18365E ${value}%, #e5e7eb ${value}%)` }}
        className="flex-1 h-2 rounded-lg appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-4
                     [&::-webkit-slider-thumb]:h-4
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-[#18365E]
                     [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-webkit-slider-thumb]:shadow-md
                     [&::-webkit-slider-thumb]:hover:bg-[#18365E]/90
                     [&::-moz-range-thumb]:w-4
                     [&::-moz-range-thumb]:h-4
                     [&::-moz-range-thumb]:rounded-full
                     [&::-moz-range-thumb]:bg-[#18365E]
                     [&::-moz-range-thumb]:border-0
                     [&::-moz-range-thumb]:cursor-pointer"
      />
        <span className="text-[10px] text-[#717182] w-7">100</span>
      </div>
    </div>
  );
};

// ============================================================
// MEASURE WEIGHT SLIDER COMPONENT (manual % + slider)
// ============================================================

interface MeasureSliderProps {
  definition: MeasureDefinition;
  value: number;
  onChange: (value: number) => void;
  isDefault: boolean;
}

const MeasureSlider: React.FC<MeasureSliderProps> = ({
  definition,
  value,
  onChange,
  isDefault,
}) => {
  return (
    <div className="py-2.5 px-3 bg-[#F5F7FA]/50 rounded-lg hover:bg-[#F5F7FA] transition-colors">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex-1 min-w-0 pr-3">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-[#18365E] truncate">{definition.label}</span>
            {!isDefault && (
              <span className="text-[8px] font-medium px-1 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">
                Modified
              </span>
            )}
          </div>
          <p className="text-[10px] text-[#717182] truncate">{definition.description}</p>
        </div>
        <div className="flex items-center border border-[#e5e7eb] rounded overflow-hidden bg-white">
          <WeightPercentInput
            value={value}
            onCommit={onChange}
            widthClass="w-10"
            className="text-sm px-1 py-1"
          />
          <span className="px-1.5 py-1 bg-[#F5F7FA] text-[10px] text-[#717182] font-medium">%</span>
        </div>
      </div>

      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ background: `linear-gradient(to right, #18365E ${value}%, #e5e7eb ${value}%)` }}
        className="w-full h-1.5 rounded-lg appearance-none cursor-pointer
                   [&::-webkit-slider-thumb]:appearance-none
                   [&::-webkit-slider-thumb]:w-3
                   [&::-webkit-slider-thumb]:h-3
                   [&::-webkit-slider-thumb]:rounded-full
                   [&::-webkit-slider-thumb]:bg-[#18365E]
                   [&::-webkit-slider-thumb]:cursor-pointer
                   [&::-webkit-slider-thumb]:shadow-sm
                   [&::-moz-range-thumb]:w-3
                   [&::-moz-range-thumb]:h-3
                   [&::-moz-range-thumb]:rounded-full
                   [&::-moz-range-thumb]:bg-[#18365E]
                   [&::-moz-range-thumb]:border-0
                   [&::-moz-range-thumb]:cursor-pointer"
      />
    </div>
  );
};

// ============================================================
// COLLAPSIBLE CATEGORY SECTION FOR MEASURES
// ============================================================

interface CategoryMeasuresSectionProps {
  definition: CategoryDefinition;
  measures: MeasureDefinition[];
  measureWeights: Record<string, number>;
  defaultMeasureWeights: Record<string, number>;
  onMeasureChange: (measureKey: string, value: number) => void;
  onNormalizeCategory: () => void;
  onResetCategory: () => void;
  isExpanded: boolean;
  onToggle: () => void;
}

const CategoryMeasuresSection: React.FC<CategoryMeasuresSectionProps> = ({
  definition,
  measures,
  measureWeights,
  defaultMeasureWeights,
  onMeasureChange,
  onNormalizeCategory,
  onResetCategory,
  isExpanded,
  onToggle,
}) => {
  const Icon = definition.icon;
  const hasCustomizations = measures.some(m =>
    measureWeights[m.key] !== defaultMeasureWeights[m.key]
  );

  const total = Object.values(measureWeights).reduce((a, b) => a + b, 0);
  const isNormalized = Math.abs(total - 100) < 0.5;

  return (
    <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden w-full">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-[#F5F7FA]/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#18365E]/10 flex items-center justify-center text-[#18365E]">
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-[#18365E]">{definition.shortLabel}</span>
              <span className="text-[10px] text-[#717182]">({measures.length} measures)</span>
              {hasCustomizations && (
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                  Modified
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded && (
            <span
              className={`text-[10px] font-medium flex items-center gap-1 ${isNormalized ? 'invisible' : 'text-amber-600'
                }`}
              aria-hidden={isNormalized}
            >
              <AlertTriangle className="w-3 h-3" />
              {Math.round(total)}%
            </span>
          )}
          {hasCustomizations && (
            <button
              onClick={(e) => { e.stopPropagation(); onResetCategory(); }}
              className="text-[10px] font-medium text-[#717182] hover:text-[#18365E] px-2 py-1 rounded hover:bg-[#F5F7FA] transition-colors"
            >
              Reset
            </button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#717182]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#717182]" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-[#e5e7eb]">
          <div className="pt-3 pb-1 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isNormalized ? (
                <span className="text-[10px] font-medium text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Total: {Math.round(total)}%
                </span>
              ) : (
                <span className="text-[10px] font-medium text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Total: {Math.round(total)}%
                </span>
              )}
              <button
                onClick={onNormalizeCategory}
                disabled={isNormalized}
                tabIndex={isNormalized ? -1 : 0}
                className="text-[10px] font-semibold text-[#FF6E4C] hover:text-[#FF6E4C]/80 
                           px-2 py-1 rounded border border-[#FF6E4C]/30 hover:bg-[#FF6E4C]/5 transition-colors
                           disabled:pointer-events-none disabled:opacity-50 disabled:cursor-default"
              >
                Normalize to 100%
              </button>
            </div>
          </div>
          <p className="text-[10px] text-[#717182]">
            Updating one measure automatically adjusts the others in this category to keep the total at 100%.
          </p>
          {measures.map((measure) => (
            <MeasureSlider
              key={measure.key}
              definition={measure}
              value={measureWeights[measure.key] ?? measure.defaultWeight}
              onChange={(value) => onMeasureChange(measure.key, value)}
              isDefault={measureWeights[measure.key] === defaultMeasureWeights[measure.key]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

export default function EAWeightsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const { session } = useAuth();
  const queryClientId = new URLSearchParams(location.search).get('clientId');
  // EA weights must use the same test client as the rest of Evaluate (300),
  // not the Firebase session client id (e.g. XpRTgoXAq5ehkSRR8zgJ).
  const clientId = EA_CLIENT_ID || queryClientId || session?.client_id || '';
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

  const {
    categoryWeights: savedCategoryWeights,
    setCategoryWeights,
    resetCategoryWeights,
    areCategoryWeightsCustomized,
    measureWeights: savedMeasureWeights,
    setMeasureWeights,
    resetMeasureWeights,
    resetAllMeasureWeights,
    areMeasureWeightsCustomized,
  } = useEA();

  // Local state for editing (allows manual entry without auto-rebalancing)
  const [localCategoryWeights, setLocalCategoryWeights] = useState<Record<string, number>>(savedCategoryWeights);
  const [localMeasureWeights, setLocalMeasureWeights] = useState<Record<string, Record<string, number>>>(savedMeasureWeights);

  // Confirm before first redistribution so users know other weights will shift.
  // Refs only — do not mirror these into state. Allow sets them on pointerdown;
  // a render that still had acknowledged=false used to overwrite the refs and
  // re-open the dialog.
  const [pendingWeightChange, setPendingWeightChange] = useState<PendingWeightChange | null>(null);
  const [redistributeDialogOpen, setRedistributeDialogOpen] = useState(false);
  const pendingWeightChangeRef = useRef<PendingWeightChange | null>(null);
  const hasAcknowledgedCategoryRedistributionRef = useRef(false);
  const hasAcknowledgedMeasureRedistributionRef = useRef(false);
  const allowingRedistributionRef = useRef(false);

  // Save status for PUT API
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Refs for debounced save — always reflect the latest local weights
  const latestCategoryWeightsRef = useRef(localCategoryWeights);
  const latestMeasureWeightsRef = useRef(localMeasureWeights);
  latestCategoryWeightsRef.current = localCategoryWeights;
  latestMeasureWeightsRef.current = localMeasureWeights;
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state when saved state changes (e.g., after reset)
  useEffect(() => {
    setLocalCategoryWeights(savedCategoryWeights);
  }, [savedCategoryWeights]);

  useEffect(() => {
    setLocalMeasureWeights(savedMeasureWeights);
  }, [savedMeasureWeights]);

  useEffect(() => {
    if (!clientId) return;

    let isCancelled = false;

    const loadWeights = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v2/weights?clientId=${encodeURIComponent(clientId)}`);
        if (!response.ok) {
          throw new Error(`Failed to load weights (${response.status})`);
        }

        const payload = await response.json();
        const data = payload?.data || payload;

        if (!data || typeof data !== 'object') {
          return;
        }

        const mappedCategoryWeights = { ...getDefaultCategoryWeightsMap() };
        Object.entries(API_CATEGORY_TO_UI_KEY).forEach(([apiKey, uiKey]) => {
          const value = Number((data as Record<string, unknown>)[apiKey]);
          if (Number.isFinite(value)) {
            mappedCategoryWeights[uiKey] = value;
          }
        });

        const normalizedCategoryWeights = normalizeWeights(mappedCategoryWeights);

        const mappedMeasureWeights = { ...getDefaultMeasureWeightsMap() };
        Object.entries(API_MEASURE_TO_UI_KEY).forEach(([apiKey, measureKey]) => {
          const categoryKey = UI_MEASURE_CATEGORY_BY_KEY[measureKey];
          if (!categoryKey) return;

          const value = Number((data as Record<string, unknown>)[apiKey]);
          if (!Number.isFinite(value)) return;

          mappedMeasureWeights[categoryKey] = {
            ...mappedMeasureWeights[categoryKey],
            [measureKey]: Math.round(value),
          };
        });

        Object.keys(mappedMeasureWeights).forEach((categoryKey) => {
          mappedMeasureWeights[categoryKey] = normalizeWeights(mappedMeasureWeights[categoryKey]);
        });

        if (isCancelled) return;

        setCategoryWeights(normalizedCategoryWeights);
        setMeasureWeights(mappedMeasureWeights);
      } catch (error) {
        console.error('Failed to load weight configuration from API:', error);
      }
    };

    loadWeights();

    return () => {
      isCancelled = true;
    };
  }, [API_BASE_URL, clientId, setCategoryWeights, setMeasureWeights]);

  // PUT: persist weight changes to backend
  const saveWeightsToAPI = useCallback(async (
    catWeights: Record<string, number>,
    measWeights: Record<string, Record<string, number>>
  ) => {
    if (!clientId) return;
    setSaveStatus('saving');
    try {
      const payload = buildApiPayload(catWeights, measWeights);
      const response = await fetch(
        `${API_BASE_URL}/api/v2/weights?clientId=${encodeURIComponent(clientId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error(`Save failed (${response.status})`);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save weight configuration:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [API_BASE_URL, clientId]);

  // Debounced save: waits 1.5s after the last change before calling PUT
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveWeightsToAPI(latestCategoryWeightsRef.current, latestMeasureWeightsRef.current);
    }, 1500);
  }, [saveWeightsToAPI]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('categories');

  // Expanded categories for measure weights
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['costSavings']));

  // Get default weights for comparison
  const defaultCategoryWeights = getDefaultCategoryWeightsMap();
  const defaultMeasureWeights = getDefaultMeasureWeightsMap();

  // Calculate totals
  const categoryTotal = Object.values(localCategoryWeights).reduce((a, b) => a + b, 0);
  const isCategoryNormalized = Math.abs(categoryTotal - 100) < 0.5;

  // Toggle category expansion (columns are independent — no row height coupling)
  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  };

  const measureCategoryEntries = Object.entries(CATEGORY_DEFINITIONS);
  const leftColumnCategories = measureCategoryEntries.filter((_, index) => index % 2 === 0);
  const rightColumnCategories = measureCategoryEntries.filter((_, index) => index % 2 === 1);

  const renderCategoryMeasuresSection = (categoryKey: string, definition: CategoryDefinition) => {
    const measures = MEASURE_DEFINITIONS[categoryKey] || [];
    const catMeasureWeights = localMeasureWeights[categoryKey] || {};
    const defaultCatMeasureWeights = defaultMeasureWeights[categoryKey] || {};

    return (
      <CategoryMeasuresSection
        key={categoryKey}
        definition={definition}
        measures={measures}
        measureWeights={catMeasureWeights}
        defaultMeasureWeights={defaultCatMeasureWeights}
        onMeasureChange={(measureKey, value) =>
          handleMeasureWeightChange(categoryKey, measureKey, value)
        }
        onNormalizeCategory={() => handleNormalizeMeasureWeights(categoryKey)}
        onResetCategory={() => handleResetCategoryMeasures(categoryKey)}
        isExpanded={expandedCategories.has(categoryKey)}
        onToggle={() => toggleCategory(categoryKey)}
      />
    );
  };

  const returnTo = state?.returnTo || '/ea/opportunities';
  const backLabel = returnTo.includes('/personalization')
    ? 'Personalize'
    : 'All opportunities';

  const handleBack = () => {
    const restoreScrollY = state?.restoreScrollY;
    const returnToTab =
      state?.returnToTab
      ?? (returnTo.includes('/opportunities') ? 'explore' : undefined);

    navigate(returnTo, {
      state: {
        ...(restoreScrollY !== undefined ? { restoreScrollY } : {}),
        ...(returnToTab ? { returnToTab } : {}),
      },
    });
  };

  // Category weight handlers - auto-distribute to maintain 100% total
  // IMPORTANT: apply the full map once via setCategoryWeights.
  // Do NOT loop updateCategoryWeight — each call rebalances again and corrupts the values.
  const applyCategoryWeightChange = useCallback((key: string, value: number) => {
    const newWeights = autoDistributeWeights(latestCategoryWeightsRef.current, key, value);
    setLocalCategoryWeights(newWeights);
    setCategoryWeights(newWeights);
    scheduleSave();
  }, [scheduleSave, setCategoryWeights]);

  const handleCategoryWeightChange = (key: string, value: number) => {
    const current = latestCategoryWeightsRef.current[key] ?? 0;
    if (Math.round(value) === Math.round(current)) return;
    if (allowingRedistributionRef.current) return;

    if (hasAcknowledgedCategoryRedistributionRef.current) {
      applyCategoryWeightChange(key, value);
      return;
    }

    // Replace any pending change (allows re-typing before Allow)
    const next = { kind: 'category' as const, key, value };
    pendingWeightChangeRef.current = next;
    setPendingWeightChange(next);
    setRedistributeDialogOpen(true);
  };

  const handleNormalizeCategoryWeights = () => {
    const normalized = normalizeWeights(localCategoryWeights);
    setLocalCategoryWeights(normalized);
    setCategoryWeights(normalized);
    scheduleSave();
  };

  const handleResetSingleCategory = (categoryKey: string) => {
    const defaultValue = defaultCategoryWeights[categoryKey];
    handleCategoryWeightChange(categoryKey, defaultValue);
  };

  const handleResetAllCategoryWeights = () => {
    resetCategoryWeights();
    const defaults = getDefaultCategoryWeightsMap();
    setLocalCategoryWeights(defaults);
    scheduleSave();
  };

  // Measure weight handlers - auto-distribute to maintain 100% total per category
  // Same rule as categories: set the full map once (no per-key rebalance loop).
  const applyMeasureWeightChange = useCallback((categoryKey: string, measureKey: string, value: number) => {
    const categoryMeasures = latestMeasureWeightsRef.current[categoryKey] || {};
    const newCategoryMeasures = autoDistributeWeights(categoryMeasures, measureKey, value);
    const newWeights = {
      ...latestMeasureWeightsRef.current,
      [categoryKey]: newCategoryMeasures
    };
    setLocalMeasureWeights(newWeights);
    setMeasureWeights(newWeights);
    scheduleSave();
  }, [scheduleSave, setMeasureWeights]);

  const handleMeasureWeightChange = (categoryKey: string, measureKey: string, value: number) => {
    const current = latestMeasureWeightsRef.current[categoryKey]?.[measureKey] ?? 0;
    if (Math.round(value) === Math.round(current)) return;
    if (allowingRedistributionRef.current) return;

    if (hasAcknowledgedMeasureRedistributionRef.current) {
      applyMeasureWeightChange(categoryKey, measureKey, value);
      return;
    }

    const next = { kind: 'measure' as const, categoryKey, measureKey, value };
    pendingWeightChangeRef.current = next;
    setPendingWeightChange(next);
    setRedistributeDialogOpen(true);
  };

  const acknowledgePending = (pending: PendingWeightChange) => {
    if (pending.kind === 'category') {
      hasAcknowledgedCategoryRedistributionRef.current = true;
    } else {
      hasAcknowledgedMeasureRedistributionRef.current = true;
    }
  };

  const handleAllowRedistribution = () => {
    const pending = pendingWeightChangeRef.current ?? pendingWeightChange;
    allowingRedistributionRef.current = true;
    if (pending) acknowledgePending(pending);
    pendingWeightChangeRef.current = null;
    setPendingWeightChange(null);
    setRedistributeDialogOpen(false);
    if (pending?.kind === 'category') {
      applyCategoryWeightChange(pending.key, pending.value);
    } else if (pending?.kind === 'measure') {
      applyMeasureWeightChange(pending.categoryKey, pending.measureKey, pending.value);
    }
    window.setTimeout(() => {
      allowingRedistributionRef.current = false;
    }, 300);
  };

  const handleCancelRedistribution = () => {
    if (allowingRedistributionRef.current) return;
    pendingWeightChangeRef.current = null;
    setPendingWeightChange(null);
    setRedistributeDialogOpen(false);
  };

  const handleNormalizeMeasureWeights = (categoryKey: string) => {
    const categoryMeasures = localMeasureWeights[categoryKey] || {};
    const normalized = normalizeWeights(categoryMeasures);
    const newWeights = { ...localMeasureWeights, [categoryKey]: normalized };
    setLocalMeasureWeights(newWeights);
    setMeasureWeights(newWeights);
    scheduleSave();
  };

  const handleResetCategoryMeasures = (categoryKey: string) => {
    resetMeasureWeights(categoryKey);
    setLocalMeasureWeights(prev => ({
      ...prev,
      [categoryKey]: defaultMeasureWeights[categoryKey]
    }));
    scheduleSave();
  };

  const handleResetAllMeasureWeights = () => {
    resetAllMeasureWeights();
    setLocalMeasureWeights(getDefaultMeasureWeightsMap());
    scheduleSave();
  };

  // Check if single category is default
  const isCategoryDefault = (categoryKey: string) => {
    return localCategoryWeights[categoryKey] === defaultCategoryWeights[categoryKey];
  };

  const saveStatusLabel =
    saveStatus === 'saving' ? (
      <span className="flex items-center gap-1 text-[10px] text-[#717182]">
        <Loader2 className="w-3 h-3 animate-spin" />
        Saving...
      </span>
    ) : saveStatus === 'saved' ? (
      <span className="flex items-center gap-1 text-[10px] text-green-600">
        <Check className="w-3 h-3" />
        Saved
      </span>
    ) : saveStatus === 'error' ? (
      <span className="flex items-center gap-1 text-[10px] text-red-500">
        <AlertTriangle className="w-3 h-3" />
        Save failed
      </span>
    ) : null;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="px-6 pt-3 pb-2">
        <EAJourneyBar />
      </div>

      <div className="sticky top-[72px] z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2.5">
        <div className="max-w-[1280px] mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {backLabel}
          </button>
          <span className="text-[#e5e7eb]" aria-hidden="true">|</span>
          <span className="text-xs font-bold text-[#18365E] truncate">Weight Settings</span>
          {saveStatusLabel}
          {(areCategoryWeightsCustomized || areMeasureWeightsCustomized) && (
            <button
              type="button"
              onClick={() => {
                handleResetAllCategoryWeights();
                handleResetAllMeasureWeights();
              }}
              className="ml-auto flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg hover:border-[#18365E] transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset All to Defaults
            </button>
          )}
        </div>
      </div>

      <main className="max-w-[1280px] mx-auto px-6 py-6 space-y-4">
        <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
          <div className="p-6 pb-5">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] font-semibold text-[#FF6E4C] uppercase tracking-wide">
                    Scoring
                  </span>
                </div>
                <h1 className="text-2xl font-bold text-[#18365E] leading-tight">Weight Settings</h1>
                <p className="text-sm text-[#717182] mt-1">
                  Fine-tune how DVA ranks opportunities for your organization. Changes apply immediately.
                </p>
              </div>
            </div>

            <div className="mt-5 flex items-center gap-1 p-1 bg-[#18365E]/[0.06] rounded-xl w-fit">
              <button
                type="button"
                onClick={() => setActiveTab('categories')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === 'categories'
                    ? 'bg-[#18365E] text-white shadow-sm'
                    : 'text-[#18365E]/70 hover:text-[#18365E] hover:bg-white/60'
                }`}
              >
                <Scale className={`w-3.5 h-3.5 ${activeTab === 'categories' ? 'text-white' : 'text-[#18365E]/50'}`} />
                Category Weights
                {areCategoryWeightsCustomized && (
                  <span className={`w-2 h-2 rounded-full ${activeTab === 'categories' ? 'bg-amber-300' : 'bg-amber-500'}`} />
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('measures')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === 'measures'
                    ? 'bg-[#18365E] text-white shadow-sm'
                    : 'text-[#18365E]/70 hover:text-[#18365E] hover:bg-white/60'
                }`}
              >
                <Layers className={`w-3.5 h-3.5 ${activeTab === 'measures' ? 'text-white' : 'text-[#18365E]/50'}`} />
                Measure Weights
                {areMeasureWeightsCustomized && (
                  <span className={`w-2 h-2 rounded-full ${activeTab === 'measures' ? 'bg-amber-300' : 'bg-amber-500'}`} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Category Weights Tab */}
        {activeTab === 'categories' && (
          <div>
            {/* Header Row with Help */}
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#18365E]">Category Weights</span>
                <HelpPopover helpKey="category_weights" />
              </div>
              <div className="flex items-center gap-3">
                {/* Total Indicator */}
                {isCategoryNormalized ? (
                  <span className="text-sm font-bold text-green-600 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Total: {Math.round(categoryTotal)}%
                  </span>
                ) : (
                  <span className="text-sm font-bold text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Total: {Math.round(categoryTotal)}%
                  </span>
                )}

                {/* Normalize Button */}
                {/* <button
                  onClick={handleNormalizeCategoryWeights}
                  disabled={isCategoryNormalized}
                  tabIndex={isCategoryNormalized ? -1 : 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white 
                             bg-[#FF6E4C] hover:bg-[#FF6E4C]/90 rounded-lg transition-colors shadow-sm
                             disabled:pointer-events-none disabled:opacity-50 disabled:cursor-default"
                >
                  Normalize to 100%
                </button> */}

                {/* Reset */}
                {areCategoryWeightsCustomized && (
                  <button
                    onClick={handleResetAllCategoryWeights}
                    className="text-xs font-medium text-[#717182] hover:text-[#18365E] transition-colors"
                  >
                    Reset All
                  </button>
                )}
              </div>
            </div>

            <div className="mb-3 rounded-lg border border-[#18365E]/10 bg-[#18365E]/5 px-3 py-2 text-xs text-[#717182]">
              Changing one category weight automatically redistributes the others to keep the total at 100%.
            </div>

            {/* Category Sliders Grid - 3 columns on large screens */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(CATEGORY_DEFINITIONS).map(([key, definition]) => {
                const pendingValue =
                  pendingWeightChange?.kind === 'category' && pendingWeightChange.key === key
                    ? pendingWeightChange.value
                    : null;
                return (
                <CategorySlider
                  key={key}
                  categoryKey={key}
                  definition={definition}
                  value={pendingValue ?? localCategoryWeights[key] ?? definition.defaultWeight}
                  onChange={handleCategoryWeightChange}
                  onReset={() => handleResetSingleCategory(key)}
                  isDefault={isCategoryDefault(key)}
                />
                );
              })}
            </div>

            {/* Dimension Legend - Compact */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-[#e5e7eb]">
              <div className="flex flex-wrap items-center gap-6">
                <span className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide">Dimensions:</span>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded bg-[#18365E]/30" />
                  <span className="text-xs text-[#717182]">
                    <strong className="text-[#18365E]">Business Value</strong> — Financial impact, strategic fit, speed to results
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded bg-[#FF6E4C]/30" />
                  <span className="text-xs text-[#717182]">
                    <strong className="text-[#FF6E4C]">Ease of Implementation</strong> — Data readiness, compliance, organizational capacity
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Measure Weights Tab */}
        {activeTab === 'measures' && (
          <div>
            {/* Header Row with Help */}
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#18365E]">Measure Weights by Category</span>
                <HelpPopover helpKey="measure_weights" />
              </div>
              {areMeasureWeightsCustomized && (
                <button
                  onClick={handleResetAllMeasureWeights}
                  className="text-xs font-medium text-[#FF6E4C] hover:text-[#FF6E4C]/80 transition-colors"
                >
                  Reset All Measures
                </button>
              )}
            </div>

            <div className="mb-3 rounded-lg border border-[#18365E]/10 bg-[#18365E]/5 px-3 py-2 text-xs text-[#717182]">
              Changing one measure weight automatically redistributes the others in that category to keep the total at 100%.
            </div>

            {/* Two independent columns — expanding left does not stretch right */}
            <div className="flex flex-col lg:flex-row gap-3 items-start">
              <div className="flex-1 min-w-0 space-y-3 w-full">
                {leftColumnCategories.map(([categoryKey, definition]) =>
                  renderCategoryMeasuresSection(categoryKey, definition),
                )}
              </div>
              <div className="flex-1 min-w-0 space-y-3 w-full">
                {rightColumnCategories.map(([categoryKey, definition]) =>
                  renderCategoryMeasuresSection(categoryKey, definition),
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {redistributeDialogOpen && (
      <AlertDialog
        open
        onOpenChange={(open) => {
          if (!open && !allowingRedistributionRef.current) handleCancelRedistribution();
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#18365E]">
              Other weights will adjust
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#717182]">
              {pendingWeightChange?.kind === 'measure'
                ? 'Changing this measure weight will automatically redistribute the other measures in this category so the total stays at 100%. Do you want to continue?'
                : 'Changing this weight will automatically redistribute the other weights so the total stays at 100%. Do you want to continue?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelRedistribution}>
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              onPointerDown={(e) => {
                // Prevent the percent input from blurring into a new pending change.
                e.preventDefault();
                handleAllowRedistribution();
              }}
              onClick={handleAllowRedistribution}
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-[#18365E] text-white hover:bg-[#18365E]/90"
            >
              Allow
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}
    </div>
  );
}